import { useEffect, useMemo, useState } from 'react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { toast } from '../hooks/use-toast';
import { exportEntity360ReportPdf } from '../lib/entity360ReportPdf';
import { NextBestQueryPanel } from './NextBestQueryPanel';
import { fmtTs, getTz } from '../lib/globalTz';
import { NativeResponseActions } from './NativeResponseActions';

type EntityType = 'user' | 'device' | 'ip' | 'domain' | 'url' | 'file_hash' | 'process' | 'mailbox' | 'cloud_app' | 'cve';
type SectionId = 'incidents' | 'alerts' | 'vulnerabilities' | 'signins' | 'network' | 'ti' | 'timeline';
type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

interface ProfileSection {
  id: SectionId;
  title: string;
  description: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  warnings: string[];
  skipped?: boolean;
}

interface RelatedEntity {
  type: string;
  value: string;
  count: number;
  sources: string[];
}

interface RecommendedAction {
  priority: 'Immediate' | 'High' | 'Medium' | 'Low';
  title: string;
  details: string;
  kql?: string;
}

interface Entity360Profile {
  customerId: string;
  entityType: EntityType;
  entityValue: string;
  lookback: string;
  generatedAt: string;
  cacheHit?: boolean;
  cacheExpiresAt?: string | null;
  risk: {
    score: number;
    level: RiskLevel;
    reasons: string[];
    counters: Record<string, number>;
  };
  summary: {
    incidents: number;
    alerts: number;
    vulnerabilities: number;
    signins: number;
    networkEvents: number;
    tiMatches: number;
    timelineEvents: number;
    relatedEntities: number;
  };
  sections: ProfileSection[];
  relatedEntities: RelatedEntity[];
  recommendedActions: RecommendedAction[];
  diagnostics: string[];
}

interface EntityInventoryRecord {
  type: EntityType;
  value: string;
  count: number;
  riskScore: number;
  lastSeen: string | null;
  sources: string[];
  signalBreakdown: Record<string, number>;
  summary: string;
}

interface EntityInventoryCategory {
  id: string;
  type: EntityType;
  label: string;
  description: string;
  total: number;
  records: EntityInventoryRecord[];
  warnings: string[];
}

interface EntityInventoryPayload {
  customerId: string;
  lookback: string;
  limit: number;
  generatedAt: string;
  expiresAt: string | null;
  cacheHit?: boolean;
  categories: EntityInventoryCategory[];
  diagnostics: string[];
}

interface EntityAiAnalysis {
  generatedAt: string;
  model: string;
  fallback: boolean;
  warnings: string[];
  analysis: string;
}

interface MlEntityBaselineInsight {
  customerId: string;
  entityKey: string;
  entityType: string;
  baselineWindow: string;
  observationCount: number;
  averageDailyIncidents: number;
  currentIncidentCount: number;
  recurrenceScore: number;
  anomalyScore: number;
  firstSeen: string | null;
  lastSeen: string | null;
  explanation: string[];
  updatedAt: string | null;
}

interface Entity360TabProps {
  onRunKql?: (query: string) => void;
}

const ENTITY_TYPES: Array<{ id: EntityType; label: string; placeholder: string; examples: string }> = [
  { id: 'user', label: 'User', placeholder: 'user@contoso.com', examples: 'UPN, username or display name' },
  { id: 'device', label: 'Device', placeholder: 'HOST-123', examples: 'Device name, host name or device ID' },
  { id: 'ip', label: 'IP Address', placeholder: '8.8.8.8', examples: 'Public/private IPv4 address' },
  { id: 'domain', label: 'Domain', placeholder: 'example.com', examples: 'Domain or FQDN' },
  { id: 'url', label: 'URL', placeholder: 'https://example.com/path', examples: 'Full or partial URL' },
  { id: 'file_hash', label: 'File Hash', placeholder: 'SHA256 / SHA1 / MD5', examples: 'MD5, SHA1 or SHA256 hash' },
  { id: 'process', label: 'Process', placeholder: 'powershell.exe', examples: 'Process name or command-line fragment' },
  { id: 'mailbox', label: 'Mailbox', placeholder: 'mailbox@contoso.com', examples: 'Mailbox address or display name' },
  { id: 'cloud_app', label: 'Cloud App', placeholder: 'Office 365 Exchange Online', examples: 'App display name or resource' },
  { id: 'cve', label: 'CVE', placeholder: 'CVE-2026-12345', examples: 'CVE identifier' },
];

const LOOKBACKS = [
  { id: '1h', label: '1h' },
  { id: '4h', label: '4h' },
  { id: '8h', label: '8h' },
  { id: '12h', label: '12h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '60d', label: '60d' },
  { id: '90d', label: '90d' },
];

const SECTION_ORDER: SectionId[] = ['incidents', 'alerts', 'vulnerabilities', 'signins', 'network', 'ti', 'timeline'];

const RISK_CLASSES: Record<RiskLevel, string> = {
  Low: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900',
  Medium: 'border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900',
  High: 'border-orange-200 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-200 dark:border-orange-900',
  Critical: 'border-red-200 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900',
};

const PRIORITY_CLASSES: Record<RecommendedAction['priority'], string> = {
  Immediate: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-900',
  High: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-900',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900',
  Low: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700',
};

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  user: 'User',
  device: 'Device',
  ip: 'IP',
  domain: 'Domain',
  url: 'URL',
  file_hash: 'File Hash',
  process: 'Process',
  mailbox: 'Mailbox',
  cloud_app: 'Cloud App',
  cve: 'CVE',
};

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(cellText).filter(Boolean).join(', ');
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

function compact(value: unknown, max = 180): string {
  const text = cellText(value).replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function safeFileName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100) || 'entity-360-export';
}

function readDispositionFilename(value: string | null): string | null {
  if (!value) return null;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf?.[1]) {
    try { return decodeURIComponent(utf[1].replace(/["']/g, '')); } catch { return utf[1].replace(/["']/g, ''); }
  }
  return /filename="?([^";]+)"?/i.exec(value)?.[1] ?? null;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.setTimeout(() => window.URL.revokeObjectURL(url), 500);
  }
}

function defaultValueForType(type: EntityType): string {
  if (type === 'cve') return 'CVE-';
  return '';
}

function preferredColumns(section: ProfileSection): string[] {
  const preferred: Record<SectionId, string[]> = {
    incidents: ['TimeGenerated', 'IncidentNumber', 'Title', 'Severity', 'Status', 'ProviderName', 'AlertsCount', 'Owner'],
    alerts: ['TimeGenerated', 'AlertName', 'Severity', 'ProductName', 'ProviderName', 'CompromisedEntity', 'SystemAlertId', 'Description'],
    vulnerabilities: ['TimeGenerated', 'Source', 'DeviceName', 'CveId', 'VulnerabilitySeverityLevel', 'CvssScore', 'ExploitLevel', 'SoftwareVendor', 'SoftwareName', 'SoftwareVersion', 'RecommendedSecurityUpdate'],
    signins: ['TimeGenerated', 'UserPrincipalName', 'IPAddress', 'AppDisplayName', 'ResultType', 'ResultDescription', 'ConditionalAccessStatus', 'RiskLevelAggregated'],
    network: ['TimeGenerated', 'SourceTable', 'DeviceName', 'ActionType', 'RemoteIP', 'RemoteUrl', 'LocalIP', 'RemotePort', 'Protocol', 'InitiatingProcessFileName'],
    ti: ['TimeGenerated', 'SourceTable', 'ObservableValue', 'ThreatType', 'ConfidenceScore', 'Active', 'ExpirationDateTime', 'Description'],
    timeline: ['TimeGenerated', 'EventType', 'Source', 'Title', 'Severity', 'Actor', 'Details'],
  };
  const all = section.columns.length > 0 ? section.columns : Array.from(section.rows.reduce((set, row) => {
    Object.keys(row).forEach((key) => set.add(key));
    return set;
  }, new Set<string>()));
  const chosen = preferred[section.id].filter((column) => all.includes(column));
  const extras = all.filter((column) => !chosen.includes(column)).slice(0, 12);
  return [...chosen, ...extras].slice(0, 18);
}

function buildColumns(section: ProfileSection): FilterableColumnDef<Record<string, unknown>>[] {
  return preferredColumns(section).map((column) => ({
    key: column,
    header: column,
    headerText: column,
    value: (row) => row[column],
    render: (row) => (
      <span className="block max-w-[520px] truncate" title={cellText(row[column])}>{compact(row[column], 240)}</span>
    ),
  }));
}

function buildInventoryColumns(): FilterableColumnDef<Record<string, unknown>>[] {
  const columns = ['Type', 'Value', 'RiskScore', 'Count', 'LastSeen', 'Sources', 'Summary'];
  return columns.map((column) => ({
    key: column,
    header: column,
    headerText: column,
    value: (row) => row[column],
    render: (row) => (
      <span className="block max-w-[520px] truncate" title={cellText(row[column])}>{compact(row[column], column === 'Summary' ? 260 : 160)}</span>
    ),
  }));
}

function buildPivotKql(type: EntityType, value: string): string {
  const escaped = value.replace(/'/g, "''");
  return [
    `let entityValue = '${escaped}';`,
    `// Entity 360 pivot for ${type}: ${escaped}`,
    'union isfuzzy=true',
    '(SecurityIncident | where TimeGenerated > ago(30d) | extend Entities_s=tostring(column_ifexists("Entities", "")) | where Entities_s contains entityValue or Title contains entityValue | project TimeGenerated, Source="SecurityIncident", Title, Severity=tostring(column_ifexists("Severity", "")), Status=tostring(Status), Details=Entities_s),',
    '(SecurityAlert | where TimeGenerated > ago(30d) | extend Entities_s=tostring(column_ifexists("Entities", "")), AlertName_s=tostring(column_ifexists("AlertName", column_ifexists("DisplayName", ""))) | where Entities_s contains entityValue or AlertName_s contains entityValue or Description contains entityValue | project TimeGenerated, Source="SecurityAlert", Title=AlertName_s, Severity=tostring(column_ifexists("AlertSeverity", column_ifexists("Severity", ""))), Status=tostring(column_ifexists("Status", "")), Details=Entities_s),',
    '(DeviceNetworkEvents | extend EventTime=todatetime(column_ifexists("Timestamp", now())) | where EventTime > ago(30d) | where RemoteIP == entityValue or RemoteUrl contains entityValue or DeviceName contains entityValue or InitiatingProcessCommandLine contains entityValue | project TimeGenerated=EventTime, Source="DeviceNetworkEvents", Title=ActionType, Severity="Informational", Status="", Details=strcat(DeviceName," ",RemoteIP," ",RemoteUrl," ",InitiatingProcessFileName)),',
    '(SigninLogs | where TimeGenerated > ago(30d) | where UserPrincipalName contains entityValue or IPAddress == entityValue or AppDisplayName contains entityValue | project TimeGenerated, Source="SigninLogs", Title=AppDisplayName, Severity=iif(ResultType == 0,"Informational","Medium"), Status=tostring(ResultType), Details=strcat(UserPrincipalName," ",IPAddress," ",ResultDescription))',
    '| order by TimeGenerated desc',
  ].join('\n');
}

function metricCards(profile: Entity360Profile): Array<{ id: SectionId | 'related'; label: string; value: number; hint: string }> {
  return [
    { id: 'incidents', label: 'Incidents', value: profile.summary.incidents, hint: 'Sentinel incident linkage' },
    { id: 'alerts', label: 'Alerts', value: profile.summary.alerts, hint: 'SecurityAlert matches' },
    { id: 'vulnerabilities', label: 'Vulnerabilities', value: profile.summary.vulnerabilities, hint: 'Defender TVM records' },
    { id: 'signins', label: 'Sign-ins', value: profile.summary.signins, hint: 'Entra sign-in activity' },
    { id: 'network', label: 'Network Events', value: profile.summary.networkEvents, hint: 'Endpoint/firewall/syslog activity' },
    { id: 'ti', label: 'TI Matches', value: profile.summary.tiMatches, hint: 'Threat-intel indicator hits' },
    { id: 'timeline', label: 'Timeline Events', value: profile.summary.timelineEvents, hint: 'Merged chronology' },
    { id: 'related', label: 'Related Entities', value: profile.summary.relatedEntities, hint: 'Extracted pivots' },
  ];
}

function inventoryRows(category: EntityInventoryCategory): Array<Record<string, unknown>> {
  return category.records.map((record) => ({
    Type: ENTITY_TYPE_LABELS[record.type] ?? record.type,
    Value: record.value,
    RiskScore: record.riskScore,
    Count: record.count,
    LastSeen: record.lastSeen ?? '',
    Sources: record.sources.join(', '),
    Summary: record.summary,
    SignalBreakdown: JSON.stringify(record.signalBreakdown ?? {}),
  }));
}

function renderMarkdownLite(markdown: string) {
  return markdown.split(/\r?\n/).map((line, idx) => {
    const key = `${idx}-${line.slice(0, 20)}`;
    if (!line.trim()) return <div key={key} className="h-2" />;
    if (line.startsWith('## ')) return <h4 key={key} className="mt-4 text-sm font-black text-foreground">{line.slice(3)}</h4>;
    if (line.startsWith('# ')) return <h3 key={key} className="mt-3 text-base font-black text-foreground">{line.slice(2)}</h3>;
    const bullet = /^(?:[-*]|\d+\.)\s+(.*)$/.exec(line.trim());
    if (bullet) return <div key={key} className="ml-3 text-sm text-muted-foreground">• {bullet[1].replace(/\*\*/g, '')}</div>;
    return <p key={key} className="text-sm text-muted-foreground">{line.replace(/\*\*/g, '')}</p>;
  });
}

function riskTone(score: number): string {
  if (score >= 80) return 'text-red-500';
  if (score >= 60) return 'text-orange-500';
  if (score >= 35) return 'text-amber-500';
  return 'text-emerald-500';
}

export function Entity360Tab({ onRunKql }: Entity360TabProps) {
  const { activeCustomerId, activeCustomer } = useCustomers();
  const { canCapability } = useAuth();
  const customerId = activeCustomerId !== 'default' ? activeCustomerId : activeCustomer?.id;
  const customerName = activeCustomer?.name ?? activeCustomerId ?? 'Customer';
  const [entityType, setEntityType] = useState<EntityType>('user');
  const [entityValue, setEntityValue] = useState('');
  const [lookback, setLookback] = useState('30d');
  const [profile, setProfile] = useState<Entity360Profile | null>(null);
  const [inventory, setInventory] = useState<EntityInventoryPayload | null>(null);
  const [inventoryLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<SectionId | 'related' | null>(null);
  const [activeInventoryCategoryId, setActiveInventoryCategoryId] = useState<string | null>(null);
  const [filteredRows, setFilteredRows] = useState<Array<Record<string, unknown>>>([]);
  const [aiAnalysis, setAiAnalysis] = useState<EntityAiAnalysis | null>(null);
  const [inventoryExpanded, setInventoryExpanded] = useState(false);
  const [mlBaselines, setMlBaselines] = useState<MlEntityBaselineInsight[]>([]);
  const [mlBaselineLoading, setMlBaselineLoading] = useState(false);
  const [mlBaselineError, setMlBaselineError] = useState<string | null>(null);

  const canExport = canCapability('entity_360', 'export_csv');
  const canPdf = canCapability('entity_360', 'export_pdf');
  const canAnalyze = canCapability('entity_360', 'analyze_profile');
  const canInventory = canCapability('entity_360', 'view_entity_inventory');
  const canRefresh = canCapability('entity_360', 'refresh_cache');
  const canTimeline = canCapability('entity_360', 'view_timeline');
  const canRelated = canCapability('entity_360', 'view_related_entities');
  const canActions = canCapability('entity_360', 'view_recommended_actions');
  const canOpenHunting = canCapability('entity_360', 'open_advanced_hunting') && canCapability('advanced_hunting', 'set_query');
  const canNextBestQuery = canCapability('next_best_query', 'generate');
  const canMlInsights = canCapability('ml_insights', 'read');

  const selectedSection = useMemo(() => {
    if (!profile || !activeSectionId || activeSectionId === 'related') return null;
    return profile.sections.find((s) => s.id === activeSectionId) ?? null;
  }, [activeSectionId, profile]);

  const selectedInventoryCategory = useMemo(() => {
    if (!inventory || !activeInventoryCategoryId) return null;
    return inventory.categories.find((c) => c.id === activeInventoryCategoryId) ?? null;
  }, [activeInventoryCategoryId, inventory]);

  const relatedRows = useMemo(() => (profile?.relatedEntities ?? []).map((entity) => ({
    Type: entity.type,
    Value: entity.value,
    Count: entity.count,
    Sources: entity.sources.join(', '),
  })), [profile?.relatedEntities]);

  useEffect(() => {
    if (!customerId || !canInventory || !inventoryExpanded) return;
    void loadInventory(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, lookback, canInventory, inventoryExpanded]);

  useEffect(() => {
    if (!customerId || !profile || !canMlInsights) { setMlBaselines([]); return; }
    let cancelled = false;
    setMlBaselineLoading(true);
    setMlBaselineError(null);
    apiFetch<{ ok: boolean; baselines: MlEntityBaselineInsight[] }>(`/ml-insights/entity/${encodeURIComponent(profile.entityValue.toLowerCase())}?customerId=${encodeURIComponent(customerId)}`).then((response) => {
      if (!cancelled) setMlBaselines(Array.isArray(response.baselines) ? response.baselines : []);
    }).catch((err) => {
      if (!cancelled) {
        setMlBaselines([]);
        setMlBaselineError(err instanceof Error ? err.message : String(err));
      }
    }).finally(() => { if (!cancelled) setMlBaselineLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, profile?.entityValue, canMlInsights, profile]);

  async function loadProfile(next?: { type?: EntityType; value?: string }, opts?: { refresh?: boolean }) {
    if (!customerId) {
      setError('Select a customer before loading Entity 360.');
      return;
    }
    const type = next?.type ?? entityType;
    const value = (next?.value ?? entityValue).trim();
    if (!value) {
      setError('Enter an entity value to investigate.');
      return;
    }
    setLoading(true);
    setError(null);
    setActiveSectionId(null);
    setActiveInventoryCategoryId(null);
    setAiAnalysis(null);
    try {
      const refreshSuffix = opts?.refresh ? '&refresh=1' : '';
      const data = await apiFetch<Entity360Profile>(`/entity-360/profile?customerId=${encodeURIComponent(customerId)}&entityType=${encodeURIComponent(type)}&entityValue=${encodeURIComponent(value)}&lookback=${encodeURIComponent(lookback)}${refreshSuffix}`);
      setProfile(data);
      setEntityType(type);
      setEntityValue(value);
      toast({ title: data.cacheHit ? 'Entity 360 profile loaded from cache' : 'Entity 360 profile loaded', description: `${data.risk.level} risk / score ${data.risk.score}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Entity 360 failed', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function loadInventory(refresh = false) {
    if (!customerId || !canInventory) return;
    setInventoryLoading(true);
    setInventoryError(null);
    try {
      const data = await apiFetch<EntityInventoryPayload>(`/entity-360/inventory?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}&limit=${inventoryLimit}${refresh ? '&refresh=1' : ''}`);
      setInventory(data);
      if (refresh) toast({ title: 'Entity summary refreshed', description: `${data.categories.length} entity categories updated.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setInventoryError(message);
      if (refresh) toast({ title: 'Entity summary refresh failed', description: message, variant: 'destructive' });
    } finally {
      setInventoryLoading(false);
    }
  }

  async function exportRows(title: string, rows: Array<Record<string, unknown>>) {
    if (!customerId) return;
    try {
      const resp = await fetch(`${getApiBase()}/entity-360/export-csv?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, rows }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { message?: string; error?: string };
        throw new Error(body.message || body.error || `Export failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const filename = readDispositionFilename(resp.headers.get('content-disposition')) || `${safeFileName(title)}.csv`;
      downloadBlob(blob, filename);
      toast({ title: 'CSV exported', description: `${rows.length.toLocaleString()} row(s) exported.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'CSV export failed', description: message, variant: 'destructive' });
    }
  }

  async function analyzeProfile() {
    if (!customerId || !profile) return;
    setAiBusy(true);
    try {
      const data = await apiFetch<EntityAiAnalysis>(`/entity-360/analyze?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      });
      setAiAnalysis(data);
      toast({ title: data.fallback ? 'Entity analysis generated with fallback' : 'AI entity analysis generated', description: data.model });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'AI analysis failed', description: message, variant: 'destructive' });
    } finally {
      setAiBusy(false);
    }
  }

  async function exportPdf() {
    if (!customerId || !profile) return;
    setPdfBusy(true);
    try {
      const fileName = await exportEntity360ReportPdf({
        customerName,
        generatedAt: new Date().toISOString(),
        tz: getTz(),
        profile,
        aiAnalysisMarkdown: aiAnalysis?.analysis ?? null,
      });
      await apiFetch<{ ok: boolean }>(`/entity-360/pdf-exported?customerId=${encodeURIComponent(customerId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType: profile.entityType, entityValue: profile.entityValue, lookback: profile.lookback, riskScore: profile.risk.score, includedAiAnalysis: Boolean(aiAnalysis?.analysis) }),
      });
      toast({ title: 'Entity 360 PDF exported', description: fileName });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'PDF export failed', description: message, variant: 'destructive' });
    } finally {
      setPdfBusy(false);
    }
  }

  function openRelated(entity: RelatedEntity) {
    const match = ENTITY_TYPES.find((t) => t.id === entity.type) ?? ENTITY_TYPES.find((t) => t.id === 'domain');
    const nextType = (match?.id ?? 'domain') as EntityType;
    setEntityType(nextType);
    setEntityValue(entity.value);
    void loadProfile({ type: nextType, value: entity.value });
  }

  function openInventoryRecord(record: EntityInventoryRecord) {
    setEntityType(record.type);
    setEntityValue(record.value);
    void loadProfile({ type: record.type, value: record.value });
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Investigation Center</div>
            <h2 className="text-2xl font-bold text-foreground">Entity 360 Universal Profile</h2>
            <p className="text-sm text-muted-foreground max-w-4xl mt-1">
              Search any user, device, IP, domain, URL, file hash, process, mailbox, cloud app or CVE and pivot across incidents, alerts,
              vulnerabilities, sign-ins, network activity, threat intelligence, timeline, related entities and recommended actions.
            </p>
          </div>
          {profile && (
            <div className={`rounded-2xl border px-5 py-3 min-w-[220px] ${RISK_CLASSES[profile.risk.level]}`}>
              <div className="text-xs uppercase tracking-wide font-bold">Entity Risk Score</div>
              <div className="flex items-end gap-3">
                <div className="text-4xl font-black leading-none">{profile.risk.score}</div>
                <div className="pb-1 font-bold">{profile.risk.level}</div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[220px_1fr_150px_auto]">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Entity type</label>
            <select
              className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              value={entityType}
              onChange={(event) => {
                const nextType = event.target.value as EntityType;
                setEntityType(nextType);
                if (!entityValue.trim()) setEntityValue(defaultValueForType(nextType));
              }}
            >
              {ENTITY_TYPES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Entity value</label>
            <input
              className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
              placeholder={ENTITY_TYPES.find((t) => t.id === entityType)?.placeholder}
              value={entityValue}
              onChange={(event) => setEntityValue(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') void loadProfile(); }}
            />
            <div className="mt-1 text-[11px] text-muted-foreground">{ENTITY_TYPES.find((t) => t.id === entityType)?.examples}</div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Lookback</label>
            <select className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" value={lookback} onChange={(event) => setLookback(event.target.value)}>
              {LOOKBACKS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 self-end">
            <button
              type="button"
              onClick={() => void loadProfile()}
              disabled={loading}
              className="rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold shadow hover:opacity-90 disabled:opacity-60"
            >
              {loading ? 'Loading…' : 'Build Profile'}
            </button>
            {profile && canRefresh && (
              <button type="button" onClick={() => void loadProfile(undefined, { refresh: true })} disabled={loading} className="rounded-xl border border-border px-4 py-2.5 text-sm font-bold hover:bg-muted disabled:opacity-60">Refresh Profile</button>
            )}
            {profile && canAnalyze && (
              <button type="button" onClick={() => void analyzeProfile()} disabled={aiBusy} className="rounded-xl border border-primary/40 px-4 py-2.5 text-sm font-bold text-primary hover:bg-primary/10 disabled:opacity-60">{aiBusy ? 'Analyzing…' : 'AI Analysis'}</button>
            )}
            {profile && canPdf && (
              <button type="button" onClick={() => void exportPdf()} disabled={pdfBusy} className="rounded-xl border border-border px-4 py-2.5 text-sm font-bold hover:bg-muted disabled:opacity-60">{pdfBusy ? 'Exporting…' : 'Export PDF'}</button>
            )}
          </div>
        </div>
        {profile && (
          <div className="mt-3 text-xs text-muted-foreground">
            Generated {fmtTs(profile.generatedAt)} · {profile.cacheHit ? 'Loaded from Entity 360 DB cache' : 'Freshly queried'}{profile.cacheExpiresAt ? ` · Cache expires ${fmtTs(profile.cacheExpiresAt)}` : ''}
          </div>
        )}
        {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}
      </div>

      {canInventory && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Entity Behavior Explorer</div>
              <h3 className="text-lg font-bold text-foreground">Top Entity Summary Cards</h3>
              <p className="text-sm text-muted-foreground">Collapsed by default. Expand to view the top 5 UEBA-style pivots for users, devices, IPs, domains, URLs, processes, file hashes, cloud apps and CVEs.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold">Top 5</span>
              {canRefresh && inventoryExpanded && <button type="button" onClick={() => void loadInventory(true)} disabled={inventoryLoading} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted disabled:opacity-60">{inventoryLoading ? 'Refreshing…' : 'Refresh Entity Map'}</button>}
              <button type="button" onClick={() => setInventoryExpanded((value) => !value)} className="rounded-xl bg-primary px-3 py-2 text-sm font-bold text-primary-foreground shadow hover:opacity-90">{inventoryExpanded ? 'Collapse Cards' : 'Show Cards'}</button>
            </div>
          </div>
          {inventoryExpanded ? (
            <>
              {inventoryError && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{inventoryError}</div>}
              {inventory && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Generated {fmtTs(inventory.generatedAt)} · {inventory.cacheHit ? 'Loaded from Entity 360 DB cache' : 'Fresh data cached'}{inventory.expiresAt ? ` · Cache expires ${fmtTs(inventory.expiresAt)}` : ''}
                </div>
              )}
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {(inventory?.categories ?? []).map((category) => (
                  <div key={category.id} className="rounded-2xl border border-border bg-background p-4 shadow-sm">
                    <button type="button" onClick={() => { setActiveInventoryCategoryId(category.id); setActiveSectionId(null); }} className="w-full text-left">
                      <div>
                        <div className="font-bold text-foreground">{category.label}</div>
                        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{category.description}</div>
                      </div>
                    </button>
                    <div className="mt-3 space-y-2">
                      {category.records.slice(0, 5).map((record) => (
                        <button key={`${category.id}-${record.value}`} type="button" onClick={() => openInventoryRecord(record)} className="w-full rounded-xl border border-border bg-card/80 px-3 py-2 text-left hover:bg-muted/60">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 font-mono text-xs text-foreground truncate" title={record.value}>{record.value}</div>
                            <div className={`text-xs font-black ${riskTone(record.riskScore)}`}>{record.riskScore}</div>
                          </div>
                          <div className="mt-1 text-[11px] text-muted-foreground truncate">{record.count.toLocaleString()} signal(s) · {record.sources.join(', ')}</div>
                        </button>
                      ))}
                      {category.records.length === 0 && <div className="rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground">No data to display for this category.</div>}
                    </div>
                  </div>
                ))}
                {inventoryLoading && !inventory && <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">Loading top 5 entity summary cards…</div>}
              </div>
            </>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">Entity summary cards are collapsed. Click Show Cards to load the top 5 cached pivots.</div>
          )}
        </div>
      )}

      {selectedInventoryCategory && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-foreground">{selectedInventoryCategory.label}</h3>
              <p className="text-sm text-muted-foreground">{selectedInventoryCategory.description}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canExport && <button type="button" onClick={() => void exportRows(`entity-360-${selectedInventoryCategory.id}`, filteredRows.length ? filteredRows : inventoryRows(selectedInventoryCategory))} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Export CSV</button>}
              <button type="button" onClick={() => setActiveInventoryCategoryId(null)} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Close</button>
            </div>
          </div>
          <div className="mt-4">
            <FilterableColumnTable
              rows={inventoryRows(selectedInventoryCategory)}
              columns={buildInventoryColumns()}
              storageKey={`entity360:inventory:${selectedInventoryCategory.id}:${customerId}`}
              rowKey={(row, idx) => `${selectedInventoryCategory.id}-${idx}-${cellText(row.Value)}`}
              maxHeightClassName="max-h-[620px]"
              onFilteredRowsChange={setFilteredRows}
              onRowClick={(row) => {
                const rawType = String(row.Type ?? '').toLowerCase().replace(/ /g, '_');
                const match = ENTITY_TYPES.find((item) => item.label.toLowerCase().replace(/ /g, '_') === rawType || item.id === rawType);
                const nextType = match?.id ?? selectedInventoryCategory.type;
                const value = cellText(row.Value);
                if (value) void loadProfile({ type: nextType, value });
              }}
              rowClassName={() => 'border-b border-border hover:bg-muted/50 cursor-pointer'}
              emptyMessage="No rows match the active Entity 360 inventory filters."
            />
          </div>
          {selectedInventoryCategory.warnings.length > 0 && (
            <details className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">
              <summary className="cursor-pointer font-bold">Inventory diagnostics</summary>
              <ul className="mt-2 list-disc pl-5 space-y-1">
                {selectedInventoryCategory.warnings.map((warning, idx) => <li key={`${warning}-${idx}`}>{warning}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {!profile && !loading && (
        <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">
          Select an entity type, enter a value, or click a top entity above to build the first Entity 360 profile.
        </div>
      )}

      {profile && (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {metricCards(profile).map((card) => (
              <button
                key={card.id}
                type="button"
                onClick={() => { setActiveSectionId(card.id); setActiveInventoryCategoryId(null); }}
                className={`text-left rounded-2xl border p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${activeSectionId === card.id ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}
              >
                <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{card.label}</div>
                <div className="mt-2 text-3xl font-black text-foreground">{card.value.toLocaleString()}</div>
                <div className="mt-1 text-xs text-muted-foreground">{card.hint}</div>
              </button>
            ))}
          </div>

          {canMlInsights && (
            <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-5 shadow-sm dark:border-violet-900 dark:bg-violet-950/20">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-violet-700 font-black dark:text-violet-200">ML Insights Engine</div>
                  <h3 className="text-lg font-bold text-foreground">Entity ML Baseline</h3>
                  <p className="text-sm text-muted-foreground">Entity recurrence/anomaly context from the ML Insights Engine. This supplements Entity 360 risk; it does not replace analyst judgement.</p>
                </div>
                <div className="rounded-full border border-violet-300 bg-background px-3 py-1 text-xs font-bold text-violet-700 dark:border-violet-800 dark:text-violet-200">
                  {mlBaselineLoading ? 'Loading…' : `${mlBaselines.length} baseline(s)`}
                </div>
              </div>
              {mlBaselineError && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">ML baseline unavailable: {mlBaselineError}</div>}
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {mlBaselines.map((baseline) => (
                  <div key={`${baseline.customerId}-${baseline.entityKey}`} className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-mono text-sm font-black text-foreground truncate" title={baseline.entityKey}>{baseline.entityKey}</div>
                      <div className={`rounded-full border px-2 py-0.5 text-xs font-black ${baseline.anomalyScore >= 85 ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200' : baseline.anomalyScore >= 60 ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200' : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'}`}>{baseline.anomalyScore}/100</div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                      <div><div className="font-black text-foreground">{baseline.observationCount}</div><div>obs</div></div>
                      <div><div className="font-black text-foreground">{baseline.currentIncidentCount}</div><div>active</div></div>
                      <div><div className="font-black text-foreground">{baseline.recurrenceScore}</div><div>recur</div></div>
                    </div>
                    <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                      {baseline.explanation.slice(0, 3).map((line, idx) => <li key={idx}>• {line}</li>)}
                    </ul>
                  </div>
                ))}
                {!mlBaselineLoading && mlBaselines.length === 0 && <div className="rounded-xl border border-dashed border-border bg-background p-4 text-sm text-muted-foreground md:col-span-3">No ML baseline has been generated yet for this entity. Run ML Insights Engine re-score to populate entity baselines.</div>}
              </div>
            </div>
          )}

          <NativeResponseActions customerId={profile.customerId} entityType={profile.entityType} entityValue={profile.entityValue} source="entity360" />

          <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Risk Drivers</h3>
                  <p className="text-sm text-muted-foreground">Deterministic entity risk based on active incidents, high/critical alerts, TVM exposure, failed sign-ins and TI matches.</p>
                </div>
                <span className={`rounded-full border px-3 py-1 text-xs font-bold ${RISK_CLASSES[profile.risk.level]}`}>{profile.risk.level}</span>
              </div>
              <div className="mt-4 h-3 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(4, Math.min(100, profile.risk.score))}%` }} />
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {profile.risk.reasons.map((reason, idx) => (
                  <li key={`${reason}-${idx}`} className="rounded-xl bg-muted/50 px-3 py-2 text-foreground">{reason}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Recommended Actions</h3>
                  <p className="text-sm text-muted-foreground">SOC next steps generated from the current entity context.</p>
                </div>
                {canOpenHunting && (
                  <button
                    type="button"
                    onClick={() => onRunKql?.(buildPivotKql(profile.entityType, profile.entityValue))}
                    className="rounded-xl border border-border px-3 py-1.5 text-xs font-bold hover:bg-muted"
                  >
                    Open Pivot KQL
                  </button>
                )}
              </div>
              {canActions ? (
                <div className="mt-4 space-y-3 max-h-[320px] overflow-auto pr-1">
                  {profile.recommendedActions.map((action, idx) => (
                    <div key={`${action.title}-${idx}`} className="rounded-xl border border-border bg-background p-3">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${PRIORITY_CLASSES[action.priority]}`}>{action.priority}</span>
                        <div className="font-bold text-sm text-foreground">{action.title}</div>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">{action.details}</p>
                      {action.kql && canOpenHunting && (
                        <button type="button" className="mt-2 text-xs font-semibold text-primary hover:underline" onClick={() => onRunKql?.(action.kql!)}>Use this KQL pivot</button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">Recommended actions are hidden by Permissions Master.</div>
              )}
            </div>
          </div>

          {canNextBestQuery && (
            <NextBestQueryPanel
              customerId={profile.customerId}
              context={{
                kind: profile.entityType === 'cve' ? 'cve' : profile.entityType === 'device' ? 'device' : 'entity',
                customerId: profile.customerId,
                entityType: profile.entityType,
                entityValue: profile.entityValue,
                title: `${profile.entityType}: ${profile.entityValue}`,
                value: profile.entityValue,
                type: profile.entityType,
                lookback: profile.lookback,
                extra: { risk: profile.risk, summary: profile.summary },
              }}
              onRunKql={onRunKql}
              compact
            />
          )}

          {aiAnalysis && (
            <div className="rounded-2xl border border-primary/30 bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">AI Entity Analysis</h3>
                  <p className="text-sm text-muted-foreground">Generated {fmtTs(aiAnalysis.generatedAt)} · Model: {aiAnalysis.model}{aiAnalysis.fallback ? ' · deterministic fallback' : ''}</p>
                </div>
                {canPdf && <button type="button" onClick={() => void exportPdf()} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Export PDF with Analysis</button>}
              </div>
              <div className="mt-4 space-y-1 rounded-xl border border-border bg-background p-4">
                {renderMarkdownLite(aiAnalysis.analysis)}
              </div>
              {aiAnalysis.warnings.length > 0 && <div className="mt-3 text-xs text-amber-600 dark:text-amber-300">{aiAnalysis.warnings.join(' ')}</div>}
            </div>
          )}

          {activeSectionId === 'related' && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">Related Entities</h3>
                  <p className="text-sm text-muted-foreground">Extracted from matching rows. Click any related entity to rebuild the profile around that pivot.</p>
                </div>
                {canExport && <button type="button" onClick={() => void exportRows('entity-360-related-entities', relatedRows)} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Export CSV</button>}
              </div>
              {canRelated ? (
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {profile.relatedEntities.map((entity) => (
                    <button key={`${entity.type}-${entity.value}`} type="button" onClick={() => openRelated(entity)} className="rounded-xl border border-border bg-background p-3 text-left hover:bg-muted/60">
                      <div className="flex items-center justify-between gap-3">
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase text-muted-foreground">{entity.type}</span>
                        <span className="text-xs text-muted-foreground">{entity.count} hit{entity.count === 1 ? '' : 's'}</span>
                      </div>
                      <div className="mt-2 font-mono text-sm text-foreground break-all">{entity.value}</div>
                      <div className="mt-1 text-xs text-muted-foreground truncate" title={entity.sources.join(', ')}>{entity.sources.join(', ')}</div>
                    </button>
                  ))}
                  {profile.relatedEntities.length === 0 && <div className="text-sm text-muted-foreground">No related entities were extracted.</div>}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">Related entities are hidden by Permissions Master.</div>
              )}
            </div>
          )}

          {selectedSection && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">{selectedSection.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedSection.description}</p>
                  {selectedSection.skipped && <div className="mt-2 text-xs text-amber-600 dark:text-amber-300">This section may be unavailable because the required table is missing or inaccessible in this workspace.</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {canExport && <button type="button" onClick={() => void exportRows(`entity-360-${selectedSection.id}`, filteredRows.length ? filteredRows : selectedSection.rows)} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Export CSV</button>}
                  <button type="button" onClick={() => setActiveSectionId(null)} className="rounded-xl border border-border px-3 py-2 text-sm font-bold hover:bg-muted">Close</button>
                </div>
              </div>
              {selectedSection.id === 'timeline' && !canTimeline ? (
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">Unified timeline is hidden by Permissions Master.</div>
              ) : selectedSection.rows.length > 0 ? (
                <div className="mt-4">
                  <FilterableColumnTable
                    rows={selectedSection.rows}
                    columns={buildColumns(selectedSection)}
                    storageKey={`entity360:${selectedSection.id}:${profile.customerId}`}
                    rowKey={(row, idx) => `${selectedSection.id}-${idx}-${cellText(row.TimeGenerated)}`}
                    maxHeightClassName="max-h-[620px]"
                    onFilteredRowsChange={setFilteredRows}
                    emptyMessage="No rows match the active Entity 360 filters."
                  />
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">No records were found for this section in the selected lookback window.</div>
              )}
              {selectedSection.warnings.length > 0 && (
                <details className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">
                  <summary className="cursor-pointer font-bold">Section diagnostics</summary>
                  <ul className="mt-2 list-disc pl-5 space-y-1">
                    {selectedSection.warnings.map((warning, idx) => <li key={`${warning}-${idx}`}>{warning}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-foreground">Investigation Sections</h3>
                <p className="text-sm text-muted-foreground">Open any section below for filterable columns, value checklist filters, autosize and CSV export.</p>
              </div>
              <div className="text-xs text-muted-foreground">Generated {fmtTs(profile.generatedAt)}</div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {SECTION_ORDER.map((id) => {
                const section = profile.sections.find((s) => s.id === id);
                if (!section) return null;
                return (
                  <button key={id} type="button" onClick={() => { setActiveSectionId(id); setActiveInventoryCategoryId(null); }} className="rounded-xl border border-border bg-background p-3 text-left hover:bg-muted/60">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-sm text-foreground">{section.title}</div>
                      <div className="text-lg font-black text-foreground">{section.rows.length}</div>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{section.description}</div>
                    {section.skipped && <div className="mt-2 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 px-2 py-0.5 text-[11px] inline-block">Partial / unavailable</div>}
                  </button>
                );
              })}
            </div>
          </div>

          {profile.diagnostics.length > 0 && (
            <details className="rounded-2xl border border-border bg-card p-5 shadow-sm text-sm">
              <summary className="cursor-pointer font-bold text-foreground">Workspace diagnostics and partial-data warnings</summary>
              <ul className="mt-3 list-disc pl-5 space-y-1 text-muted-foreground">
                {profile.diagnostics.map((item, idx) => <li key={`${item}-${idx}`}>{item}</li>)}
              </ul>
            </details>
          )}

          {inventory?.diagnostics.length ? (
            <details className="rounded-2xl border border-border bg-card p-5 shadow-sm text-sm">
              <summary className="cursor-pointer font-bold text-foreground">Entity summary diagnostics and partial-data warnings</summary>
              <ul className="mt-3 list-disc pl-5 space-y-1 text-muted-foreground">
                {inventory.diagnostics.map((item, idx) => <li key={`${item}-${idx}`}>{item}</li>)}
              </ul>
            </details>
          ) : null}
        </>
      )}
    </div>
  );
}
